import { useMutation } from '@apollo/client'
import styled from '@emotion/styled'
import { defineMessages, FormattedMessage } from '@faceup/localization'
import { useParams } from '@faceup/router'
import { Space, Spin, Typography } from '@faceup/ui-base'
import { VerifyVictimEmailResult } from '@faceup/utils'
import { useEffect, useState } from 'react'
import { graphql } from '../__generated__'
import useRegion from '../utils/useRegion'
import PlainLayout from './PlainLayout'

const { Title, Text } = Typography

const mutations = {
  EditIsVerifiedCompanyVictim: graphql(`
    mutation EditIsVerifiedCompanyVictimMutation(
      $input: EditIsVerifiedCompanyVictimInput!
    ) {
      editIsVerifiedCompanyVictim(input: $input) {
        result
      }
    }
  `),
}

const verificationResultTitles = defineMessages(
  'followUp.components.emailVerification',
  VerifyVictimEmailResult,
  {
    [VerifyVictimEmailResult.Success]: 'Your email has been successfully verified',
    [VerifyVictimEmailResult.Error]: 'Your email address could not be verified, please try again.',
    [VerifyVictimEmailResult.EmailAlreadyVerified]: 'This e-mail has already been verified',
  }
)

const verificationResultInfos = defineMessages(
  'followUp.components.emailVerification.verificationResultInfos',
  VerifyVictimEmailResult,
  {
    [VerifyVictimEmailResult.Success]: 'You will now receive information about any changes',
    [VerifyVictimEmailResult.Error]:
      "We couldn't resend the verification email. Please refresh the page and try again.",
    [VerifyVictimEmailResult.EmailAlreadyVerified]:
      'Your email is already verified. Please refresh the page.',
  }
)

const EmailVerification = () => {
  const [result, setResult] = useState<VerifyVictimEmailResult | null>(null)
  const [wasCalled, setWasCalled] = useState(false)
  const { discoverByToken } = useRegion()
  const { id: token } = useParams<'id'>()

  const [editIsCompanyVerified, { loading }] = useMutation(mutations.EditIsVerifiedCompanyVictim, {
    variables: {
      input: {
        token: token ?? '',
      },
    },
    onCompleted: ({ editIsVerifiedCompanyVictim }) => {
      setResult(editIsVerifiedCompanyVictim?.result ?? VerifyVictimEmailResult.Error)
    },
  })

  useEffect(() => {
    const fetchToken = async () => {
      if (token && discoverByToken && editIsCompanyVerified && !wasCalled) {
        setWasCalled(true)
        await discoverByToken(token)
        await editIsCompanyVerified()
      }
    }
    fetchToken()
  }, [discoverByToken, editIsCompanyVerified, token, wasCalled])

  return (
    <PlainLayout>
      <Wrapper>
        <Space direction='vertical' align='center' size={40}>
          {loading || !wasCalled || result === null ? (
            <Spin />
          ) : (
            <Space direction='vertical' align='center' size='small'>
              <Title level={2} style={{ display: 'inline-block' }}>
                <FormattedMessage message={verificationResultTitles[result]} />
              </Title>
              <Text style={{ fontSize: 16 }}>
                <FormattedMessage message={verificationResultInfos[result]} />
              </Text>
            </Space>
          )}
        </Space>
      </Wrapper>
    </PlainLayout>
  )
}

const Wrapper = styled.div`
  margin: auto;
`

export default EmailVerification
